import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import { EuroInput } from "@/components/EuroInput";
import { Boxes, Loader2, Plus, Save, Trash2, Upload, FileText, FileSpreadsheet, Image as ImageIcon, X, Zap, RotateCcw } from "lucide-react";

const inputCls = "h-11 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[14.5px] font-semibold";

// (slug → display label)
const CATEGORY_LABELS = {
  waermepumpe:          "Wärmepumpe",
  brauchwasserspeicher: "Brauchwasserspeicher",
  heizungspuffer:       "Heizungspuffer",
  montage:              "Montage & Hydraulik",
  elektroinstallation:  "Elektroinstallation",
  fundament:            "Fundament",
  demontage:            "Demontage",
  inbetriebnahme:       "Inbetriebnahme",
  solarthermie:         "Solarthermie",
  pv_kopplung:          "PV-Kopplung",
  wartung:              "Wartung",
  sonstiges:            "Sonstiges",
};
const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS);

function newDraft() {
  return {
    id: `new-${Date.now()}`,
    category: "sonstiges",
    title: "",
    description: "",
    price_net: 0,
    vat_rate: 0.19,
    sort_order: 100,
    active: true,
    isNew: true,
    dirty: true,
  };
}

export default function AdminComponents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    api.get("/components", { params: { include_inactive: showInactive } })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Komponenten konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showInactive]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const filtered = useMemo(() => {
    const list = filter === "all" ? items : items.filter((c) => c.category === filter);
    return [...list].sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a.category);
      const bi = CATEGORY_ORDER.indexOf(b.category);
      if (ai !== bi) return ai - bi;
      return (a.sort_order ?? 100) - (b.sort_order ?? 100);
    });
  }, [items, filter]);

  const upd = (id, field, value) =>
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value, dirty: true } : c)));

  const addNew = () => setItems((prev) => [newDraft(), ...prev]);

  const save = async (c) => {
    setSavingId(c.id);
    try {
      const body = {
        category: c.category,
        title: c.title,
        description: c.description || "",
        price_net: Number(c.price_net) || 0,
        vat_rate: Number(c.vat_rate) || 0.19,
        sort_order: Number(c.sort_order) || 100,
        active: !!c.active,
      };
      if (c.isNew) {
        const r = await api.post("/admin/components", body);
        setItems((prev) => prev.map((x) => (x.id === c.id ? { ...r.data, dirty: false } : x)));
      } else {
        const r = await api.patch(`/admin/components/${c.id}`, body);
        setItems((prev) => prev.map((x) => (x.id === c.id ? { ...r.data, dirty: false } : x)));
      }
      toast.success("Gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (c) => {
    if (c.isNew) {
      setItems((prev) => prev.filter((x) => x.id !== c.id));
      return;
    }
    const msg = `Komponente „${c.title}" wirklich löschen?\n\nWenn die Komponente noch in Angeboten/Paketen verwendet wird, wird sie stattdessen deaktiviert.`;
    if (!window.confirm(msg)) return;
    try {
      // Try hard delete first
      const r = await api.delete(`/admin/components/${c.id}`, { params: { hard: true } });
      if (r.data?.hard_deleted) {
        toast.success("Komponente gelöscht");
      } else {
        toast.success("Komponente deaktiviert");
      }
      setItems((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      // 409 = referenced → fallback to soft delete
      if (e.response?.status === 409) {
        const reason = e.response?.data?.detail || "wird noch verwendet";
        if (window.confirm(`${reason}\n\nStattdessen deaktivieren (im Katalog ausblenden)?`)) {
          try {
            await api.delete(`/admin/components/${c.id}`);
            toast.success("Komponente deaktiviert (Referenzen bleiben erhalten)");
            setItems((prev) => prev.filter((x) => x.id !== c.id));
          } catch (e2) {
            toast.error(e2.response?.data?.detail || "Deaktivieren fehlgeschlagen");
          }
        }
      } else {
        toast.error(e.response?.data?.detail || "Fehler beim Löschen");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Komponenten" back="/dashboard" />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-[#00E575]/15 flex items-center justify-center"><Boxes className="text-[#00E575]" /></div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl font-semibold">Komponenten-Katalog</h1>
            <p className="text-sm text-zinc-300 font-medium">Zentral pflegbare Bauteile & Leistungen für alle WP-Pakete</p>
          </div>
          <label className="inline-flex items-center gap-1.5 text-[12px] text-zinc-300 font-medium cursor-pointer mr-1" title="Deaktivierte Komponenten einblenden">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="components-toggle-inactive"
              className="accent-[#00E575]"
            />
            Inaktive
          </label>
          <button
            data-testid="components-import-button"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#09090B] border border-zinc-700 hover:border-zinc-500 text-sm"
          >
            <Upload size={14} /> <span className="hidden sm:inline">Import</span>
          </button>
          <button
            data-testid="components-seed-demo"
            onClick={async () => {
              setSeeding(true);
              try {
                const { data } = await api.post("/admin/components/seed-demo");
                toast.success(`Demo-Seed: ${data.inserted_count} angelegt · ${data.skipped_count} übersprungen`);
                await load();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Seed fehlgeschlagen");
              } finally { setSeeding(false); }
            }}
            disabled={seeding}
            title="Basis-Komponenten (Puffer, Speicher, Montage, Elektro, IBN, Wartung) anlegen — idempotent"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#09090B] border border-amber-500/40 hover:border-amber-400 text-amber-300 font-semibold text-sm disabled:opacity-50"
          >
            {seeding ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            <span className="hidden sm:inline">Demo-Seed</span>
          </button>
          <button
            data-testid="components-add-button"
            onClick={addNew}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#00E575] text-[#09090B] font-semibold text-sm"
          >
            <Plus size={14} /> Neu
          </button>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-4">
          {["all", ...CATEGORY_ORDER].map((c) => {
            const count = c === "all" ? items.length : items.filter((x) => x.category === c).length;
            const active = filter === c;
            return (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`components-filter-${c}`}
                className={`px-3 py-1.5 rounded-full text-xs transition border ${
                  active ? "bg-[#00E575]/15 border-[#00E575] text-[#00E575]" : "bg-[#18181B] border-zinc-800 text-zinc-300 hover:border-zinc-600"
                }`}
              >
                {c === "all" ? "Alle" : CATEGORY_LABELS[c]} <span className="opacity-60 font-mono">{count}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-zinc-300 text-center font-medium py-12"><Loader2 className="animate-spin inline mr-2" /> Lade…</div>
        ) : (
          <div className="space-y-2">
            {filtered.length === 0 && (
              <div className="text-center py-12 text-zinc-400 text-sm font-medium">Keine Komponenten in dieser Kategorie.</div>
            )}
            {filtered.map((c) => (
              <div key={c.id} data-testid={`component-row-${c.id}`} className={`bg-[#18181B] border rounded-2xl p-4 relative ${c.active ? "border-zinc-800" : "border-red-900/50 opacity-70"}`}>
                {!c.isNew && !c.active && (
                  <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-md text-[12px] font-extrabold uppercase tracking-widest bg-red-900/80 text-red-100 border border-red-700">
                    Inaktiv
                  </span>
                )}
                <div className="grid grid-cols-12 gap-3 items-start">
                  <div className="col-span-12 sm:col-span-3">
                    <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Kategorie</label>
                    <select className={inputCls} value={c.category} onChange={(e) => upd(c.id, "category", e.target.value)} data-testid={`component-${c.id}-category`}>
                      {CATEGORY_ORDER.map((k) => <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>)}
                    </select>
                  </div>
                  <div className="col-span-12 sm:col-span-5">
                    <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Titel</label>
                    <input className={inputCls} value={c.title} placeholder="z. B. Pufferspeicher 500 L" onChange={(e) => upd(c.id, "title", e.target.value)} data-testid={`component-${c.id}-title`} />
                  </div>
                  <div className="col-span-12 sm:col-span-2">
                    <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Preis netto</label>
                    <EuroInput value={c.price_net} onChange={(v) => upd(c.id, "price_net", v)} testid={`component-${c.id}-price`} className={inputCls} />
                  </div>
                  <div className="col-span-12 sm:col-span-2 flex items-end gap-2 mt-1 sm:mt-0">
                    <button
                      onClick={() => save(c)}
                      disabled={savingId === c.id || !c.title}
                      data-testid={`component-${c.id}-save`}
                      className="h-10 flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#00E575] text-[#09090B] font-semibold text-xs disabled:opacity-40"
                    >
                      {savingId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      <span className="sm:hidden">Speichern</span>
                    </button>
                    {!c.isNew && !c.active && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/admin/components/${c.id}/reactivate`);
                            toast.success("Komponente reaktiviert");
                            load();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Reaktivieren fehlgeschlagen");
                          }
                        }}
                        data-testid={`component-${c.id}-reactivate`}
                        title="Komponente reaktivieren — wieder für alle sichtbar/nutzbar"
                        className="h-10 w-10 flex-shrink-0 inline-flex items-center justify-center rounded-xl bg-[#09090B] border border-[#00E575]/50 text-[#00E575] hover:border-[#00E575]"
                      >
                        <RotateCcw size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => remove(c)}
                      data-testid={`component-${c.id}-delete`}
                      className="h-10 w-10 flex-shrink-0 inline-flex items-center justify-center rounded-xl bg-[#09090B] border border-zinc-700 text-red-300 hover:border-red-500/60"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="col-span-12">
                    <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Beschreibung</label>
                    <input className={inputCls} value={c.description || ""} onChange={(e) => upd(c.id, "description", e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {importOpen && <ImportModal onClose={() => { setImportOpen(false); load(); }} />}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Import modal (CSV, Excel, PDF, Image)
// --------------------------------------------------------------------------- //
function ImportModal({ onClose }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [committing, setCommitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setSelectedFile(file);
    setError("");
    setPreview(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/components/import?commit=false", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Import-Vorschau fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!selectedFile) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      const r = await api.post("/admin/components/import?commit=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${r.data.inserted} Komponenten importiert (${r.data.skipped_duplicates || 0} Duplikate übersprungen)`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl max-w-3xl w-full max-h-[88vh] overflow-hidden flex flex-col" data-testid="components-import-modal">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="font-display text-lg font-semibold">Komponenten importieren</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-800" data-testid="components-import-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div className="text-sm text-zinc-300 font-medium">
            Unterstützte Formate:{" "}
            <span className="inline-flex items-center gap-1 mx-1"><FileSpreadsheet size={12} /> CSV</span> /
            <span className="inline-flex items-center gap-1 mx-1"><FileSpreadsheet size={12} /> Excel</span> /
            <span className="inline-flex items-center gap-1 mx-1"><FileText size={12} /> PDF</span> /
            <span className="inline-flex items-center gap-1 mx-1"><ImageIcon size={12} /> Bild</span>
            <br />
            <span className="text-xs text-zinc-400 font-medium">CSV/Excel: Spalten <code className="bg-[#09090B] px-1 rounded">Kategorie</code>, <code className="bg-[#09090B] px-1 rounded">Titel</code>, <code className="bg-[#09090B] px-1 rounded">Beschreibung</code>, <code className="bg-[#09090B] px-1 rounded">Preis netto</code>. PDF/Bild: KI-Extraktion via Gemini Vision.</span>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xlsm,.pdf,.png,.jpg,.jpeg,.webp"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="components-import-file"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            className="w-full py-6 rounded-xl border-2 border-dashed border-zinc-700 hover:border-[#00E575] hover:bg-[#00E575]/5 transition flex items-center justify-center gap-2 text-sm text-zinc-300"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {selectedFile ? selectedFile.name : "Datei wählen oder hierher ziehen"}
          </button>

          {error && (
            <div className="bg-red-900/30 border border-red-500/40 text-red-200 text-sm p-3 rounded-xl" data-testid="components-import-error">
              {error}
            </div>
          )}

          {preview && (
            <div className="space-y-2">
              <div className="text-xs text-zinc-300 font-medium">
                Erkannt: <span className="text-[#00E575] font-bold">{preview.rows.length}</span> Komponenten ·
                Format: <span className="font-mono uppercase">{preview.detected_format}</span>
              </div>
              <div className="max-h-72 overflow-y-auto border border-zinc-800 rounded-xl">
                <table className="w-full text-xs">
                  <thead className="bg-[#09090B] sticky top-0">
                    <tr className="text-left">
                      <th className="p-2 text-zinc-200 font-semibold">Kategorie</th>
                      <th className="p-2 text-zinc-200 font-semibold">Titel</th>
                      <th className="p-2 text-zinc-200 text-right font-semibold">Preis netto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className="border-t border-zinc-800">
                        <td className="p-2 text-zinc-200 font-semibold">{CATEGORY_LABELS[r.category] || r.category}</td>
                        <td className="p-2">{r.title}</td>
                        <td className="p-2 text-right font-mono">{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(r.price_net || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-[#09090B] border border-zinc-700 text-sm">Abbrechen</button>
          <button
            onClick={commit}
            disabled={!preview || preview.rows.length === 0 || committing}
            data-testid="components-import-commit"
            className="px-4 py-2 rounded-xl bg-[#00E575] text-[#09090B] font-semibold text-sm disabled:opacity-40 inline-flex items-center gap-2"
          >
            {committing ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {preview ? `${preview.rows.length} importieren` : "Importieren"}
          </button>
        </div>
      </div>
    </div>
  );
}
